import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { apiService } from "@/lib/api-service";
import { parseValidationErrors } from "@/lib/parse-validation-errors.util";
import { DEFAULT_DEPARTMENTS } from "@employee-management/common";
import type { SalesMember } from "@/types/project.types";

const projectFormSchema = z.object({
  name: z.string().min(2, "Project name must be at least 2 characters"),
  clientName: z.string().min(2, "Client name must be at least 2 characters"),
  description: z.string().optional(),
  internalProjectId: z.preprocess(
    (v) => (v === "" ? null : v),
    z.coerce.number().positive().nullable()
  ),
  startDate: z.string().min(1, "Start date is required"),
  handlerMemberId: z.string().cuid("Handler member must be selected"),
  salesTeamMemberId: z.string().cuid("Sales team member must be selected"),
});

type ProjectFormData = z.infer<typeof projectFormSchema>;

export function ProjectCreateDialog() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const {
    register,
    formState: { errors },
    setValue,
    watch,
    reset,
    handleSubmit,
    setError,
    getFieldState,
    control,
  } = useForm<ProjectFormData>({
    // @ts-expect-error - zod resolver is not typed properly for internalProjectId
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: "",
      clientName: "",
      description: "",
      internalProjectId: null,
      startDate: new Date().toISOString(), // Today's date
      handlerMemberId: "",
      salesTeamMemberId: "",
    },
  });

  const handlerMember = watch("handlerMemberId");
  const salesTeamMember = watch("salesTeamMemberId");

  const { data: salesMembers } = useQuery({
    queryKey: ["sales-members"],
    queryFn: async () =>
      apiService.get<SalesMember[]>("/employees/sales-members"),
  });

  const projectMembersOptions: ComboboxOption[] =
    salesMembers
      ?.filter((employee) => employee.department === DEFAULT_DEPARTMENTS[0])
      ?.map((employee) => ({
        value: employee.id,
        label: employee.name,
        description: `${employee.department} • ${employee.email}`,
      })) || [];

  const salesMembersOptions: ComboboxOption[] =
    salesMembers
      ?.filter((employee) => employee.department === DEFAULT_DEPARTMENTS[1])
      ?.map((employee) => ({
        value: employee.id,
        label: employee.name,
        description: `${employee.department} • ${employee.email}`,
      })) || [];

  const createProjectMutation = useMutation({
    mutationFn: async (data: ProjectFormData) => {
      // Convert string dates to Date objects for the backend
      const projectData = {
        ...data,
        startDate: new Date(data.startDate),
      };

      await apiService.post("/projects", projectData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      reset();
      setOpen(false);
    },
    onError: (error: unknown) => {
      parseValidationErrors<ProjectFormData>(error, setError, getFieldState);

      toast.error("Failed to create project");
    },
  });

  const onSubmit = (data: unknown) => {
    createProjectMutation.mutate(data as ProjectFormData);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Clear form when dialog is closed
      reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" />
          Create Project
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="internalProjectId">Project ID</Label>
              <Input
                id="internalProjectId"
                type="number"
                {...register("internalProjectId")}
                placeholder="Enter project number (optional)"
              />
              {errors.internalProjectId?.message && (
                <p className="text-sm text-destructive">
                  {errors.internalProjectId.message as string}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" required>
                Project Name
              </Label>
              <Input
                id="name"
                {...register("name")}
                placeholder="Enter project name"
              />
              {errors.name?.message && (
                <p className="text-sm text-destructive">
                  {errors.name.message as string}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="clientName" required>
                Client Name
              </Label>
              <Input
                id="clientName"
                {...register("clientName")}
                placeholder="Enter client name"
              />
              {errors.clientName?.message && (
                <p className="text-sm text-destructive">
                  {errors.clientName.message as string}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register("description")}
                placeholder="Enter project description"
                rows={3}
              />
              {errors.description?.message && (
                <p className="text-sm text-destructive">
                  {errors.description.message as string}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="startDate" required>
                Date
              </Label>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    date={field.value ? new Date(field.value) : undefined}
                    onDateChange={(date) =>
                      field.onChange(date?.toISOString() || "")
                    }
                    placeholder="Select date"
                  />
                )}
              />
              {errors.startDate?.message && (
                <p className="text-sm text-destructive">
                  {errors.startDate.message as string}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="handlerMemberId" required>
                Project Supervisor
              </Label>
              <Combobox
                options={projectMembersOptions}
                value={handlerMember}
                onValueChange={(value: string) =>
                  setValue("handlerMemberId", value)
                }
                placeholder="Select project supervisor"
              />
              {errors.handlerMemberId?.message && (
                <p className="text-sm text-destructive">
                  {errors.handlerMemberId.message as string}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="salesTeamMemberId" required>
                Project Manager
              </Label>
              <Combobox
                options={salesMembersOptions}
                value={salesTeamMember}
                onValueChange={(value: string) =>
                  setValue("salesTeamMemberId", value)
                }
                placeholder="Select project manager"
              />
              {errors.salesTeamMemberId?.message && (
                <p className="text-sm text-destructive">
                  {errors.salesTeamMemberId.message as string}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createProjectMutation.isPending}>
              {createProjectMutation.isPending
                ? "Creating..."
                : "Create Project"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
